import { Badge, Group, Loader, Menu, Text, UnstyledButton } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '../auth/useAuth.js';
import { useT } from '../i18n/useT.js';
import { AddCompanyModal } from './AddCompanyModal.js';
import { describeError } from './ErrorMessage.js';

/**
 * Which company's books are on screen.
 *
 * Visible on every page — an acceptance criterion, and the reason for it is
 * that this application shows two companies' ledgers through identical screens.
 * A person who has forgotten which one they are looking at can post an entry to
 * the wrong set of books, and postings cannot be deleted.
 *
 * Switching issues a new token rather than changing the session, so a token
 * always authorises exactly one company.
 *
 * **It has to look like a control.** It shipped as a bare `UnstyledButton` —
 * the company name and a role badge, no border, no chevron, nothing — and a
 * user with two companies reported not being able to find it. It was on screen
 * the whole time and read as a label. A test asserted it was *present*, which
 * is not the same as usable, and being present is worth nothing if nobody can
 * see that it can be clicked. It is a pill with a chevron now.
 *
 * **It also adds companies.** "Tilføj virksomhed" sits under the list, because
 * this is where a person already thinks about which companies they have. It is
 * role-gated: ADR-006 restricts company creation to `owner` or `system_admin`,
 * and this component already knows the role in every company -- a settings page
 * would have to learn it. An invited accountant does not see the control.
 *
 * **The dropdown shows names.** It used to show UUID prefixes for every company
 * except the active one, because `GET /v1/auth/companies` runs before a company
 * scope exists and RLS therefore hides `companies`. Migration 0014 added a
 * SECURITY DEFINER function that returns names for the caller's own
 * memberships, so a switcher between two companies is now a choice between two
 * names rather than between `a58a5647` and `e8f7c791`.
 */
export function CompanySwitcher(): ReactNode {
  const t = useT();
  const { memberships, companyId, role, selectCompany } = useAuth();
  const queryClient = useQueryClient();
  const [switching, setSwitching] = useState(false);
  const [adding, setAdding] = useState(false);

  /*
    Whether to offer company creation at all.

    `POST /v1/companies` is refused unless the caller holds owner or
    system_admin *somewhere* (ADR-006 §6), so the same question is asked here
    -- across every membership, not just the active one. Someone who owns one
    company and audits another may still create a third.

    Hidden rather than disabled: a disabled button invites the question "why",
    and the honest answer is "this account is not for that", which is better
    said by the control not being there.
  */
  const mayCreateCompany = memberships.some(
    (item) => item.role === 'owner' || item.role === 'system_admin',
  );

  const current = memberships.find((item) => item.companyId === companyId);
  const label = current?.name ?? current?.companyId.slice(0, 8) ?? t('company.choose');

  async function handleSelect(target: string): Promise<void> {
    if (target === companyId) {
      return;
    }
    setSwitching(true);
    try {
      await selectCompany(target);
      // Everything cached was read under the previous company's token. Keeping
      // any of it would show one company's figures under another's name, which
      // is the single worst thing this screen could do.
      await queryClient.resetQueries();
    } catch (error) {
      notifications.show({
        color: 'red',
        title: t('company.switchFailed'),
        message: describeError(error, t),
      });
    } finally {
      setSwitching(false);
    }
  }

  return (
    <>
      <Menu shadow="md" width={260} position="bottom-start">
        <Menu.Target>
          <UnstyledButton
            px="md"
            py={6}
            aria-label={t('company.switch')}
            style={{
              // A pill: fully rounded, bordered, on its own background. Without
              // an edge this was indistinguishable from the heading beside it,
              // and a user with two companies could not find it at all.
              borderRadius: 'var(--mantine-radius-xl)',
              border: '1px solid var(--mantine-color-gray-4)',
              background: 'var(--mantine-color-gray-0)',
            }}
          >
            <Group gap="xs" wrap="nowrap">
              {switching ? <Loader size="xs" /> : null}
              <Text fw={600} size="sm" lineClamp={1}>
                {label}
              </Text>
              {role !== null && (
                <Badge size="xs" variant="light" tt="none">
                  {role}
                </Badge>
              )}
              {/*
                The chevron is what says "this opens something". A text glyph
                rather than an icon package: the project has no icon library, and
                adding one for a single arrow is not worth the dependency.
              */}
              <Text size="xs" c="dimmed" aria-hidden="true">
                ▾
              </Text>
            </Group>
          </UnstyledButton>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Label>{t('company.companies')}</Menu.Label>
          {memberships.map((item) => (
            <Menu.Item
              key={item.companyId}
              onClick={() => {
                void handleSelect(item.companyId);
              }}
              disabled={switching}
              rightSection={
                item.companyId === companyId ? (
                  <Badge size="xs" variant="filled">
                    {t('company.active')}
                  </Badge>
                ) : null
              }
            >
              {/*
                The id is shown under the name for *every* named entry, not
                only where it is ambiguous. Two companies can share a name -- it
                happened on the first day of real use, when a mistyped CVR left
                two called "Sipotek ApS" -- and a switcher that cannot tell them
                apart is how an entry gets posted to the wrong set of books.

                `name` used to be null for every company but the active one:
                `companies` is company-scoped by RLS and this call runs before
                any company is chosen. Migration 0014 reads it through
                `company_names_for_user` instead, so names are there now. It can
                still be null -- a membership whose company row has gone has no
                name to return -- and the id prefix is then all there is.
              */}
              <div>
                <Text size="sm">{item.name ?? item.companyId.slice(0, 8)}</Text>
                {/*
                  Only when there is a name to disambiguate. Without one the line
                  above is already the id, and printing it twice is noise -- and
                  broke a test that looked the id up by text.
                */}
                {item.name !== null && (
                  <Text size="xs" c="dimmed" ff="monospace">
                    {item.companyId.slice(0, 8)}
                  </Text>
                )}
              </div>
            </Menu.Item>
          ))}
          {mayCreateCompany && (
              <>
                <Menu.Divider />
                <Menu.Item
                  onClick={() => {
                    setAdding(true);
                  }}
                  disabled={switching}
                >
                  <Text size="sm">{t('company.add')}</Text>
                </Menu.Item>
              </>
            )}
          </Menu.Dropdown>
      </Menu>

      <AddCompanyModal
        opened={adding}
        onClose={() => {
          setAdding(false);
        }}
      />
    </>
  );
}
